import { CornerDownRight, ListEnd, Trash2 } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Minimal queued message shape needed by the composer queue tray.
 * Keeps the queue UI independent from chat transport details.
 */
export interface MessageQueueItem {
  /** Stable client-side identifier for queue operations. */
  id: string;
  /** Text preview shown in the queue row. */
  message: string;
}

interface MessageQueueProps {
  /** Messages waiting to be sent after the active stream finishes. */
  items: MessageQueueItem[];
  /** Removes an item from the pending queue. */
  onRemove: (id: string) => void;
  /** Interrupts the current stream and sends this queued item next. */
  onSteer: (id: string) => void;
}

/**
 * Composer-attached queue tray for messages submitted while the chat is busy.
 * Renders as thin rows that visually connect to the prompt input below.
 */
export function MessageQueue({ items, onRemove, onSteer }: MessageQueueProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mx-5 max-h-32 overflow-y-auto rounded-t-lg border border-b-0 border-border bg-background shadow-sm">
      <div className="divide-y divide-border/50">
        {items.map((item) => (
          <div key={item.id} className="flex h-8 items-center gap-2 px-3 text-sm">
            <ListEnd className="size-3.5 shrink-0 text-muted-foreground" />
            <p className="min-w-0 flex-1 truncate text-muted-foreground">
              {item.message}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  onClick={() => onSteer(item.id)}
                >
                  <CornerDownRight className="size-3.5" />
                  <span>Steer</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>Steer into the current response</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => onRemove(item.id)}
                >
                  <Trash2 className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Remove from queue</TooltipContent>
            </Tooltip>
          </div>
        ))}
      </div>
    </div>
  );
}
